import React, { ReactElement, ReactNode } from "react";
import { Badge, Button, Flex, Heading, Icon, RadioGroup, Text } from "@telia/teddy";
import "./RetailerSubscriptionAccordion.less";
import { useSelector } from "react-redux";
import { getCurrentSubscriptionStep } from "../../../state/selectors/subscriptionStepsSelectors";
import { HW_BUNDLE_LEASE_CODE, RootState, SIMDEAL_CAMPAIGN_CODE } from "../../../../../../models";
import { getActiveSubscription } from "../../../../checkout/state/selectors/cartSelectors";

interface Props {
    children?: ReactNode;
    familyDiscount?: boolean;
    yearlyCommitmentLabel?: string;
    id: string;
    isActive?: boolean;
    isDisabled?: boolean;
    isEditable?: boolean;
    isSelectable?: boolean;
    isSelected?: boolean;
    name?: string | ReactElement;
    onClose?: () => void;
    onSelectSubscription?: () => void;
    price?: string | number;
    priceStriketrough?: string | number;
    priceSuffix?: string;
    status?: string;
    strikethroughTitle?: string;
    title?: string;
    validationStatus?: "success" | "warning" | null;
    hasSubscriptionChanged?: boolean;
    existingOfferingCode?: string;
    newOfferingCode?: string;
    hasSimChanged?: boolean;
    hasUserChanged?: boolean;
    hasServicesChanged?: boolean;
    isKeepExistingSubscription?: boolean;
    changedFromName?: string;
    changedToName?: string;
    isFirst?: boolean;
}

const getSubscriptionChangedText = (existingOfferingCode?: string, newOfferingCode?: string): string => {
    const [existingPricePlan] = existingOfferingCode?.split(".") ?? [];
    const [newPricePlan, newIcim] = newOfferingCode?.split(".") ?? [];
    if (existingPricePlan !== newPricePlan) return "Endret abonnement";
    if (newIcim?.startsWith(SIMDEAL_CAMPAIGN_CODE)) return "Lagt til tilbehørsbinding";
    if (newIcim?.startsWith(HW_BUNDLE_LEASE_CODE)) return "Lagt til telefonbinding";
    return "Endret abonnement";
};

const RetailerSubscriptionAccordion: React.FC<Props> = ({
    id,
    name,
    title,
    price,
    priceStriketrough,
    familyDiscount,
    yearlyCommitmentLabel,
    onSelectSubscription,
    onClose,
    priceSuffix = ",-/md.",
    isDisabled = false,
    isSelected = false,
    isSelectable = true,
    strikethroughTitle,
    children,
    isEditable,
    validationStatus,
    hasSubscriptionChanged,
    existingOfferingCode,
    newOfferingCode,
    hasSimChanged,
    hasUserChanged,
    hasServicesChanged,
    isKeepExistingSubscription = false,
    changedFromName,
    changedToName,
    isFirst = false,
}) => {
    const isChanged = hasSubscriptionChanged || hasSimChanged || hasUserChanged || hasServicesChanged;
    const currentStep = useSelector((state: RootState) => getCurrentSubscriptionStep(state, id));
    const activeSubscription = useSelector((state: RootState) => getActiveSubscription(state));
    let changedFromPrefix = "Endret fra";
    if (isKeepExistingSubscription) {
        changedFromPrefix = "";
    } else if (isSelected) {
        changedFromPrefix = "Endrer fra";
    }

    const subscriptionContent = (
        <div className="subscriptionCard__content">
            <div>
                <div className="subscriptionCard__nameWrapper">
                    <Heading mb="100" as="h2" variant="title-200" className="subscriptionCard__name" data-di-mask>
                        {strikethroughTitle && (
                            <span className="subscriptionCard__strikethroughTitle">{strikethroughTitle}</span>
                        )}
                        {name || "Nytt abonnement"}
                    </Heading>
                </div>
                {(changedFromName || (changedToName && currentStep > 0)) && (
                    <Flex gapX="100" mt="50">
                        {changedFromName && !isKeepExistingSubscription && (
                            <Text variant="paragraph-100">
                                {changedFromPrefix} {changedFromName}
                            </Text>
                        )}
                        {changedToName && !isKeepExistingSubscription && currentStep > 0 && (
                            <>
                                <Icon name="arrow-right" size="sm" aria-hidden="true" focusable="false" />
                                <Text variant="paragraph-100">{changedToName}</Text>
                            </>
                        )}
                    </Flex>
                )}
                {(!activeSubscription || currentStep === 0) && (
                    <span dangerouslySetInnerHTML={{ __html: title || "" }} />
                )}
            </div>
            <div className="subscriptionCard__priceWrapper">
                {price && (
                    <Heading
                        mb="100"
                        as="h2"
                        variant="title-200"
                        title={`${price}`}
                        className="subscriptionCard__price"
                    >
                        {price}
                        {priceSuffix}
                    </Heading>
                )}
                {priceStriketrough && (
                    <div className="subscriptionCard__priceStriketroughWrapper">
                        <span className="subscriptionCard__priceStriketroughDisclaimer">ord. pris:</span>
                        <div className="subscriptionCard__priceStriketrough">
                            {priceStriketrough}
                            {priceSuffix}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className={`subscriptionCard${isFirst ? " subscriptionCard--first" : ""}`} id={id}>
            {isChanged && (
                <Flex gap="100" wrap="wrap" className="subscriptionCard__changedBadges">
                    {hasSubscriptionChanged && (
                        <Badge variant="success">
                            {getSubscriptionChangedText(existingOfferingCode, newOfferingCode)}
                        </Badge>
                    )}
                    {hasSimChanged && <Badge variant="success">Endret SIM-kort</Badge>}
                    {hasUserChanged && <Badge variant="success">Endret bruker</Badge>}
                    {hasServicesChanged && <Badge variant="success">Tilleggstjenester endret</Badge>}
                </Flex>
            )}
            <div className="subscriptionCard__headingWrapper">
                {onClose && (
                    <div className="subscriptionCard__closeButton">
                        <Button variant="text" onClick={onClose} aria-label="Lukk">
                            <Icon name="close" aria-hidden="true" focusable="false" />
                        </Button>
                    </div>
                )}
                <div className="subscriptionCard__heading">
                    {isSelectable && !isSelected ? (
                        <RadioGroup.Item>
                            <RadioGroup.Trigger value={id} disabled={isDisabled} />
                            <RadioGroup.Label>
                                {subscriptionContent}
                                {(isEditable || validationStatus) && (
                                    <div className="subscriptionCard__actions">
                                        {isEditable && (
                                            <Icon name="edit" size="md" aria-hidden="true" focusable="false" />
                                        )}
                                        {validationStatus && (
                                            <Icon
                                                className={`subscriptionCard__validationIcon--${validationStatus}`}
                                                name={validationStatus === "success" ? "check-circle-filled" : "alert"}
                                                size="md"
                                                aria-hidden="true"
                                                focusable="false"
                                            />
                                        )}
                                    </div>
                                )}
                            </RadioGroup.Label>
                        </RadioGroup.Item>
                    ) : (
                        <div
                            className="subscriptionCard__container"
                            onClick={isSelectable ? undefined : onSelectSubscription}
                        >
                            {subscriptionContent}
                            {(isEditable || validationStatus) && (
                                <div className="subscriptionCard__actions">
                                    {isEditable && <Icon name="edit" size="md" aria-hidden="true" focusable="false" />}
                                    {validationStatus && (
                                        <Icon
                                            className={`subscriptionCard__validationIcon--${validationStatus}`}
                                            name={validationStatus === "success" ? "check-circle-filled" : "alert"}
                                            size="md"
                                            aria-hidden="true"
                                            focusable="false"
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {(familyDiscount || yearlyCommitmentLabel) && (
                <div className="subscriptionCard__badgesRow">
                    {familyDiscount && (
                        <Badge hideIcon variant="special">
                            Familierabatt
                        </Badge>
                    )}
                    {yearlyCommitmentLabel && (
                        <Badge hideIcon variant="information">
                            {yearlyCommitmentLabel}
                        </Badge>
                    )}
                </div>
            )}
            {children && isSelected && <div className="subscriptionCard__addSubscriptionSection">{children}</div>}
        </div>
    );
};

export default RetailerSubscriptionAccordion;
